import { useEffect, useLayoutEffect, useState, type RefObject } from 'react'

/**
 * Narrow-container gate for the Pro search bar (#1846).
 *
 * The desktop bar ↔ phone pill swap in explorer.css is a VIEWPORT media
 * query (932/933px). The widget renders inside a shadow root, where `@media`
 * sees the viewport and never the column the bar actually sits in — a 960px
 * Duda content column with the map open hands the bar 478px at a 1470px
 * viewport, Who/Filters keep their CSS min-widths, and Where/When collapse
 * to their padding with the labels stacked letter by letter.
 *
 * ProFilterBar measures its own wrapper (the `.hfse-filter-bar.hfse-sbp`
 * div: the one ancestor of both the form and the pill that never leaves the
 * widget tree, while the pinned form rides the sticky shell into the body
 * portal) and sets `hfse-sbp-narrow` on it when the bar cannot hold its
 * fields; the stylesheet swaps the same two nodes the 932px block swaps.
 * Same JS-set class gate the mobile-results top bar uses (`.hfse-mres`),
 * deliberately no media query and no `@container`: `container-type` adds
 * layout containment, which would re-root the position:fixed sticky shells
 * on shadow-less mounts, and it cannot see `searchbar.maxWidthPx`.
 *
 * SSR contract (AGENTS.md): the state starts false — server HTML and the
 * hydration pass render the full bar — and the first measurement runs in a
 * layout effect, so a client mount paints the right surface on its first
 * frame. An unlaid-out element (display:none up the tree, SSR, happy-dom)
 * reads 0 and means "unknown": the last answer stands (false at mount), so a
 * host tab or accordion hiding and showing the widget neither flashes the
 * collapsed bar nor churns the sticky hooks.
 */

/** 6px padding × 2 + 1px border × 2 (`.hfse-sbp-bar`, explorer.css). */
export const SBP_BAR_CHROME_PX = 14
/** `.hfse-sbp-bar { gap: 6px }` — between EVERY flex item: fields, dividers, submit. */
export const SBP_BAR_GAP_PX = 6
/** `.hfse-sbp-divider { width: 1px }`, one between consecutive fields. */
export const SBP_DIVIDER_PX = 1
/** `.hfse-sbp-submit { width: 52px }` collapsed (refine), 64px under the hero dress. */
export const SBP_SUBMIT_PX = 52
export const SBP_SUBMIT_HERO_PX = 64
/**
 * Leave-narrow margin. The swap itself cannot oscillate (the wrapper's width
 * is parent-determined), but the bar→pill height change can toggle a classic
 * scrollbar and move the column by ~15px — the one real feedback path.
 */
export const SBP_NARROW_HYSTERESIS_PX = 24

/**
 * The narrowest width each field keeps its label on one line and its value
 * readable, per layout. Who / Filters / Bedrooms are the stylesheet's own
 * min-widths (explorer.css `.hfse-sbp-guests-field` 140, `-filters-field`
 * 160, `-bedrooms-field` 118). Where and When have `min-width: 0` in CSS —
 * they are the fields that collapse — so their floors are what the default
 * 11px/15px type needs: 36px of padding plus ~80px of Where input, plus the
 * "WHEN" label. Compact (#1700) drops the label row, holds the text fields in
 * equal thirds and makes Filters a 64px icon button.
 */
export const SBP_FIELD_MIN_PX = {
  labelled: { where: 116, date: 72, guests: 140, 'additional-filters': 160, 'promoted-bedrooms': 118 },
  compact: { where: 116, date: 116, guests: 116, 'additional-filters': 64, 'promoted-bedrooms': 116 },
} as const

type FieldTable = Record<string, number>

export interface SbpBarMinWidthInput {
  /** The desktop bar's rendered field keys, in order (ProFilterBar `barFields`). */
  fields: readonly string[]
  /** `searchbar.barLayout === 'compact'`. */
  compact: boolean
  /** Hero bar: 64px submit instead of 52px. */
  hero: boolean
}

/**
 * The bar width below which the rendered fields cannot fit: chrome + submit
 * + every field's floor + the dividers between fields + a gap between every
 * pair of flex items (n fields + n−1 dividers + the submit = 2n items, so
 * 2n−1 gaps). Unknown field keys are ignored; no fields → 0 (never narrow).
 * Labelled Where/When/Who/Filters = 599, compact = 523, +Bedrooms = 730,
 * hero +12.
 */
export function sbpBarMinWidth({ fields, compact, hero }: SbpBarMinWidthInput): number {
  const table: FieldTable = compact ? SBP_FIELD_MIN_PX.compact : SBP_FIELD_MIN_PX.labelled
  const known = fields.filter((f) => Object.prototype.hasOwnProperty.call(table, f))
  const n = known.length
  if (n === 0) return 0
  const fieldsPx = known.reduce((sum, f) => sum + table[f], 0)
  return (
    SBP_BAR_CHROME_PX +
    (hero ? SBP_SUBMIT_HERO_PX : SBP_SUBMIT_PX) +
    fieldsPx +
    (n - 1) * SBP_DIVIDER_PX +
    (2 * n - 1) * SBP_BAR_GAP_PX
  )
}

/**
 * The wrapper's content-box width — what `.hfse-sbp-bar { width: 100% }`
 * resolves against. `offsetWidth` (not a rect) so a host transform cannot
 * scale the reading, same as applyPill; 0 when the element has no layout.
 */
export function readSbpAvailableWidth(el: HTMLElement): number {
  const outer = el.offsetWidth
  if (!(outer > 0)) return 0
  const cs = getComputedStyle(el)
  const padding = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0)
  return Math.max(0, outer - padding)
}

/**
 * Pure decision on the MEASURED column width: unknown (≤0) keeps `prev`;
 * entering needs `available < threshold`, leaving needs
 * `available ≥ threshold + hysteresis`.
 */
export function decideSbpNarrow(
  available: number,
  threshold: number,
  prev: boolean,
  hysteresis: number = SBP_NARROW_HYSTERESIS_PX,
): boolean {
  if (!(available > 0)) return prev
  return prev ? available < threshold + hysteresis : available < threshold
}

// Layout-phase on the client so the first client commit already carries the
// class; the server has no layout phase and warns on useLayoutEffect
// (useMobileResultsActive.ts, providers.tsx).
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

/**
 * True while the bar's wrapper cannot hold the rendered fields. Measures once
 * synchronously after mount and then follows a ResizeObserver on the wrapper
 * (feature-guarded: happy-dom has none, and the test stubs deliver `cb()`
 * with no entries, so the callback re-reads the element instead of trusting
 * an entry; applied on the next animation frame, see below). The bar never
 * grows past `searchbar.maxWidthPx`, so a cap the fields cannot fit in
 * collapses the bar in any column: that is a static condition and gets no
 * hysteresis — only the measured column width does, because comparing the
 * CAPPED reading would strand a cap inside [threshold, threshold + margin)
 * in the pill for good. The wrapper, not the bar, is observed — hiding the
 * bar changes the wrapper's height only, and the re-read yields the same
 * boolean.
 */
export function useSbpNarrowBar(
  wrapperRef: RefObject<HTMLElement | null>,
  threshold: number,
  maxWidthPx: number,
): boolean {
  const [narrow, setNarrow] = useState(false)
  useIsomorphicLayoutEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const apply = () => {
      const measured = readSbpAvailableWidth(el)
      setNarrow((prev) => {
        if (!(measured > 0)) return prev
        if (maxWidthPx < threshold) return true
        return decideSbpNarrow(measured, threshold, prev)
      })
    }
    apply()
    if (typeof ResizeObserver === 'undefined') return
    // Notifications land a frame later: the swap changes the wrapper's OWN
    // height during the observer's delivery, which WebKit reports as
    // "ResizeObserver loop completed with undelivered notifications" when the
    // state flips synchronously inside the callback. rAF-coalesced, like the
    // submit re-measure in ProFilterBar; the mount read above stays
    // synchronous so the first paint is right.
    let frame = 0
    const observer = new ResizeObserver(() => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        apply()
      })
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [wrapperRef, threshold, maxWidthPx])
  return narrow
}
